import { API_CONFIG, createApiUrl } from '../api'
import type {
  FavoriteResponse,
  FavoriteRequest,
  FavoriteCheckResponse,
  FavoriteType
} from '../../types/types.user'

export class FavoriteAPI {

  /**
   * Add a favorite
   */
  static async addFavorite(
    userId: string,
    request: FavoriteRequest,
    accessToken: string
  ): Promise<FavoriteResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.BASE.replace('{userId}', userId)
    )

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to add favorite: ${error}`)
    }

    return response.json()
  }

  /**
   * Remove a favorite
   */
  static async removeFavorite(
    userId: string,
    targetType: FavoriteType,
    targetId: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.BASE.replace('{userId}', userId)
    )

    const params = new URLSearchParams()
    params.append('targetType', targetType)
    params.append('targetId', targetId)

    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to remove favorite: ${error}`)
    }
  }

  /**
   * Toggle a favorite (add if not exists, remove if exists)
   */
  static async toggleFavorite(
    userId: string,
    request: FavoriteRequest,
    accessToken: string
  ): Promise<boolean> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.TOGGLE.replace('{userId}', userId)
    )

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to toggle favorite: ${error}`)
    }

    const result: FavoriteCheckResponse = await response.json()
    return result.isFavorited
  }

  /**
   * Check if an item is favorited
   */
  static async checkFavorite(
    userId: string,
    targetType: FavoriteType,
    targetId: string,
    accessToken: string
  ): Promise<boolean> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.CHECK.replace('{userId}', userId)
    )

    const params = new URLSearchParams()
    params.append('targetType', targetType)
    params.append('targetId', targetId)

    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to check favorite: ${error}`)
    }

    const result: FavoriteCheckResponse = await response.json()
    return result.isFavorited
  }

  /**
   * Get all favorites for a user
   */
  static async getUserFavorites(
    userId: string,
    accessToken: string
  ): Promise<FavoriteResponse[]> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.BASE.replace('{userId}', userId)
    )

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to get user favorites: ${error}`)
    }

    return response.json()
  }

  /**
   * Get favorites by type
   */
  static async getUserFavoritesByType(
    userId: string,
    targetType: FavoriteType,
    accessToken: string
  ): Promise<FavoriteResponse[]> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.BY_TYPE.replace('{userId}', userId)
    )

    const params = new URLSearchParams()
    params.append('targetType', targetType)

    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to get user favorites by type: ${error}`)
    }

    return response.json()
  }

  /**
   * Get favorite IDs by type (just the IDs, not full objects)
   */
  static async getUserFavoriteIds(
    userId: string,
    targetType: FavoriteType,
    accessToken: string
  ): Promise<string[]> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.FAVORITES.IDS.replace('{userId}', userId)
    )

    const params = new URLSearchParams()
    params.append('targetType', targetType)

    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to get user favorite IDs: ${error}`)
    }

    return response.json()
  }
}
